/**
 * BrandTestCard — try a bundle before anyone installs it.
 *
 * The panels below this card INSTALL: the moment a .zip lands there, every
 * agent in the tenant dresses its documents in it. Judging a bundle used to
 * mean installing it on a live tenant and undoing it afterwards.
 *
 * Here the same .zip goes into ONE conversation with an agent built for the
 * job — every output format and file tool already on, so the first prompt
 * cannot dead-end on "this agent cannot make a .pptx". The brand lives on
 * that conversation alone: the tenant's own brand, and everyone else's
 * documents, are untouched.
 */
import { useState } from 'react';
import { Button, Upload, message } from 'antd';
import { ExperimentOutlined, InboxOutlined, MessageOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import { useTenantPath } from '@/routing';
import {
  startBrandSandbox, type BrandSandboxSession,
} from '@/modules/super_admin/design_system/services/designSkillService';

interface Props {
  /** The tenant the bundle is tried against — assets are written under it. */
  tenantId: string;
}

export default function BrandTestCard({ tenantId }: Props) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState<BrandSandboxSession | null>(null);

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const started = await startBrandSandbox(tenantId, file);
      setSession(started);
      message.success(t('systemDesign.brandTest.ready', { name: started.manifest.name || file.name }));
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      message.error(detail || t('systemDesign.brandTest.failed'));
    } finally {
      setBusy(false);
    }
  };

  const open = () => {
    if (!session) return;
    navigate(tenantPath(`/chat?conversation=${session.conversation_id}`));
  };

  return (
    <section className="wl-section" data-testid="brand-test-card">
      <header className="wl-section-header">
        <h4 className="wl-section-title">
          <ExperimentOutlined className="wl-section-icon" /> {t('systemDesign.brandTest.title')}
        </h4>
        <p className="wl-section-subtitle">{t('systemDesign.brandTest.subtitle')}</p>
      </header>
      <div className="wl-section-body">
        {session ? (
          <>
            <div className="wl-test-summary" data-testid="brand-test-summary">
              <strong dir="auto">{session.manifest.name || session.manifest.source_file_name}</strong>
              <span>
                {t('systemDesign.brandTest.bundleMeta', {
                  assets: session.manifest.asset_count,
                  size: Math.max(1, Math.round(session.manifest.size_bytes / 1024)),
                })}
              </span>
            </div>
            <div className="wl-test-actions">
              <Button type="primary" icon={<MessageOutlined />} onClick={open}
                      data-testid="brand-test-open">
                {t('systemDesign.brandTest.open')}
              </Button>
              <Upload
                accept=".zip"
                showUploadList={false}
                beforeUpload={(file) => { void upload(file as File); return false; }}
              >
                <Button loading={busy} data-testid="brand-test-replace">
                  {t('systemDesign.brandTest.another')}
                </Button>
              </Upload>
            </div>
            <p className="wl-section-why">{t('systemDesign.brandTest.scopeNote')}</p>
          </>
        ) : (
          <Upload.Dragger
            accept=".zip"
            showUploadList={false}
            disabled={busy}
            beforeUpload={(file) => { void upload(file as File); return false; }}
            data-testid="brand-test-dropzone"
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">{t('systemDesign.brandTest.dropTitle')}</p>
            <p className="ant-upload-hint">{t('systemDesign.brandTest.dropHint')}</p>
          </Upload.Dragger>
        )}
      </div>
    </section>
  );
}
